import { platform } from "@/utils/platform";
import AKJS from "@akc/akjs";
import { useCallback, useEffect, useRef } from "react";

export const getUserId = async () => {
  if (platform.isApp) {
    const res: any = await AKJS.ready().then(() => {
      // account() 返回结构体：http://git.akcops.com/bfe-infra/jsbridge/docs/-/blob/master/akc/User.md
      return AKJS.info.account();
    });
    return res?.data?.userID || "anonymous";
  }
  try {
    // 从 localStorage 获取 userinfo 字符串
    const userInfoStr = localStorage.getItem("userinfo");

    // 如果存在 userinfo，解析 JSON 并获取 openId
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      if (userInfo && userInfo.openId) {
        return userInfo.openId;
      }
    }

    // 如果没有找到有效的 openId，返回默认值
    return "anonymous";
  } catch (error) {
    // 处理 JSON 解析错误或其他异常
    console.error("获取用户ID失败:", error);
    return "anonymous";
  }
};

const STORAGE_PREFIX = "one_time_execution";

/**
 * @description 一个用于确保某个操作在每个用户上只执行一次的 Hook。
 * @param {string} key - 用于标识该操作的唯一字符串。
 * @param {() => void | Promise<void>} callback - 当条件满足时要执行的回调函数，可以是同步或异步的。
 * @param {boolean} [enabled=true] - 是否启用该 hook。默认为 true。
 * @returns {[() => Promise<void>]} 返回一个手动触发函数。
 */
const useOneTimeExecution = (
  key: string,
  callback: () => void | Promise<void>,
  enabled = true,
): [() => Promise<void>] => {
  const callbackRef = useRef(callback);

  // 始终在 ref 中保持最新的 callback
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const execute = useCallback(async () => {
    if (!key) {
      return;
    }

    try {
      const userId = await getUserId();
      const storageKey = `${STORAGE_PREFIX}_${userId}_${key}`;
      const hasExecuted = localStorage.getItem(storageKey);

      if (hasExecuted !== "Y") {
        // 从 ref 中调用 callback，确保总是最新的
        await Promise.resolve(callbackRef.current());
        localStorage.setItem(storageKey, "Y");
        console.log(`[useOneTimeExecution] Callback for key "${key}" executed and marked as done.`);
      }
    } catch (error) {
      // 如果回调执行失败，不应标记为已执行，以便下次重试
      console.error(`[useOneTimeExecution] Failed to execute callback for key "${key}":`, error);
    }
  }, [key]); // 移除了对 callback 的依赖

  useEffect(() => {
    if (enabled) {
      execute();
    }
  }, [execute, enabled]);

  return [execute];
};

export default useOneTimeExecution;
